import React from 'react';

import * as Sentry from '@sentry/browser';
import { ZonedDateTime } from '@js-joda/core';
import { Chip } from '@opendoor/bricks/complex';
import { Box, Flex } from '@opendoor/bricks/core';

import { DAY_OF_WEEK_AND_TIME, systemZonedDateTimeFromString } from 'shared/helpers/datetime';

import { BadgeWithoutIcon } from 'buying/components/propertyCards/BadgeWithoutIcon';
import { badgeData, ListingStatus } from 'buying/components/propertyCards/listingStatus';
import { OpenHouse } from 'buying/graphql/useProperties';

interface ListingBadgesProps {
  badges: ListingStatus[];
  displayMax?: number;
  openHouses: OpenHouse[] | null;
}

const generateText = (
  badgeType: ListingStatus,
  badgeTitle: string | undefined,
  openHouses: OpenHouse[],
): string | undefined => {
  const firstOpenHouseStartsAt =
    openHouses.length > 0 ? systemZonedDateTimeFromString(openHouses[0].startsAt) : null;
  if (
    badgeType === 'open' &&
    firstOpenHouseStartsAt &&
    // first open house should be now or in the future
    !firstOpenHouseStartsAt.isBefore(ZonedDateTime.now())
  ) {
    return systemZonedDateTimeFromString(openHouses[0].startsAt)
      .format(DAY_OF_WEEK_AND_TIME)
      .replace(':00', '')
      .replace('AM', 'am')
      .replace('PM', 'pm');
  }

  return badgeTitle;
};

/** Listing badges for PropertyCard */
export const ListingBadges: React.FC<ListingBadgesProps> = ({
  badges,
  openHouses = [],
  displayMax = 2,
}) => {
  return (
    <Flex flexDirection="column" className="listing-badges">
      {/* display max of 2 badges per property by default */}
      {badges.slice(0, displayMax).map((badge) => {
        if (!badgeData[badge]) {
          Sentry.captureException(`Unsupported listing badge name: ${badge}`);
          return;
        }
        const currentIcon = badgeData[badge].icon;
        const currentText = generateText(badge, badgeData[badge].title, openHouses || []);
        return (
          <Box width="max-content" key={`listing-badges-${badge}`} data-testid="badge-div">
            {currentIcon ? (
              <Chip
                borderColor="neutrals20"
                leftIcon={{
                  name: currentIcon,
                  color: badgeData[badge].color,
                  viewBox: badgeData[badge].viewBox,
                }}
                // To do:text will be required in the future please fix usages that show only the icon in the chip
                text={currentText}
                textColor={badgeData[badge].color}
                backgroundColor={badgeData[badge].backgroundColor || 'neutrals0'}
                size={badgeData[badge].size ? 'small' : 'large'}
              />
            ) : (
              <BadgeWithoutIcon
                backgroundColor={badgeData[badge].backgroundColor || 'neutrals0'}
                currentText={currentText || ''}
                color={badgeData[badge].color}
              />
            )}
          </Box>
        );
      })}
    </Flex>
  );
};
